import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { scrollToId } from "./SmoothScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const QUESTIONS = [
  {
    key: "goal",
    q: "What are you here to solve?",
    hint: "Pick the concern that bothers you most.",
    options: ["Dark spots & melasma", "Acne & breakouts", "Fine lines & aging", "Overall glow & texture"],
  },
  {
    key: "skin_type",
    q: "How would you describe your skin?",
    hint: "This helps us gauge strength.",
    options: ["Oily", "Dry", "Combination", "Sensitive"],
  },
  {
    key: "tone",
    q: "What's your skin tone?",
    hint: "Pigment behaves differently across tones.",
    options: ["Fair", "Medium", "Olive", "Deep / rich"],
  },
  {
    key: "used_retinoid",
    q: "Have you used a retinoid before?",
    hint: "Beginners start gentler.",
    options: ["Never", "A little", "Regularly", "I'm a pro"],
  },
  {
    key: "sensitivity",
    q: "Any known sensitivities?",
    hint: "We screen for safety.",
    options: ["None", "Mild irritation", "Rosacea", "Prefer not to say"],
  },
];

function Countdown() {
  const [s, setS] = useState(15 * 60);
  useEffect(() => {
    const t = setInterval(() => setS((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return (
    <span className="font-mono text-[var(--terra)]" data-testid="offer-countdown">
      {mm}:{ss}
    </span>
  );
}

export default function IntakeModal({ open, onClose }) {
  const [step, setStep] = useState(0); // 0..n questions, then analyzing, then result
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const total = QUESTIONS.length;
  const phase = step < total ? "quiz" : step === total ? "email" : step === total + 1 ? "analyzing" : "done";

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0);
        setAnswers({});
        setEmail("");
      }, 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (phase !== "analyzing") return;
    axios.post(`${API}/intake`, { answers, email: email || null }).catch(() => {});
    const t = setTimeout(() => setStep((s) => s + 1), 2600);
    return () => clearTimeout(t);
  }, [phase]); // eslint-disable-line

  const pick = (opt) => {
    const cur = QUESTIONS[step];
    setAnswers((a) => ({ ...a, [cur.key]: opt }));
    setTimeout(() => setStep((s) => s + 1), 260);
  };

  const progress = Math.min(step / (total + 2), 1);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10001] flex items-stretch"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="intake-modal"
        >
          <div className="absolute inset-0 bg-[var(--ink)]" />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="relative w-full text-[var(--bone)] flex flex-col"
          >
            {/* top bar */}
            <div className="flex items-center justify-between px-5 sm:px-8 h-16 sm:h-20 border-b border-white/10">
              <span className="font-display text-2xl">Balance <span className="text-[var(--terra)]">+ Beauty</span></span>
              <span className="overline text-white/50 hidden sm:block">Eligibility consult</span>
              <button onClick={onClose} className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors" data-testid="intake-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            {/* progress */}
            <div className="h-px bg-white/10">
              <motion.div className="h-full bg-[var(--terra)]" animate={{ width: `${progress * 100}%` }} transition={{ ease: EASE }} />
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll">
              <div className="max-w-3xl mx-auto w-full px-5 sm:px-8 py-14 sm:py-24 min-h-full flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  {phase === "quiz" && (
                    <motion.div key={step} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5, ease: EASE }}>
                      <span className="font-mono text-[var(--terra-soft)] text-sm">0{step + 1} / 0{total}</span>
                      <h3 className="font-display text-4xl sm:text-6xl leading-[0.95] tracking-tight mt-5">{QUESTIONS[step].q}</h3>
                      <p className="font-body text-white/50 mt-4">{QUESTIONS[step].hint}</p>
                      <div className="grid sm:grid-cols-2 gap-3 mt-10">
                        {QUESTIONS[step].options.map((o) => (
                          <button
                            key={o}
                            onClick={() => pick(o)}
                            className={`text-left font-body text-lg px-6 py-5 border transition-colors ${
                              answers[QUESTIONS[step].key] === o
                                ? "bg-[var(--bone)] text-[var(--ink)] border-[var(--bone)]"
                                : "border-white/20 hover:border-white/60"
                            }`}
                            data-testid={`intake-opt-${step}`}
                          >
                            {o}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {phase === "email" && (
                    <motion.div key="email" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5, ease: EASE }}>
                      <span className="font-mono text-[var(--terra-soft)] text-sm">Last step</span>
                      <h3 className="font-display text-4xl sm:text-6xl leading-[0.95] tracking-tight mt-5">Where should we send your result?</h3>
                      <p className="font-body text-white/50 mt-4">We'll analyze your answers and unlock your eligible pricing.</p>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="mt-10 w-full bg-transparent border-b border-white/30 focus:border-[var(--terra)] outline-none py-4 font-body text-2xl placeholder:text-white/30"
                        data-testid="intake-email"
                      />
                      <button
                        onClick={() => setStep((s) => s + 1)}
                        className="mt-10 btn-ink !bg-[var(--bone)] !text-[var(--ink)] px-8 py-4 rounded-full inline-flex items-center gap-2"
                        data-testid="intake-analyze-btn"
                      >
                        Analyze my skin <ArrowRight size={16} />
                      </button>
                    </motion.div>
                  )}

                  {phase === "analyzing" && (
                    <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center" data-testid="intake-analyzing">
                      <motion.div
                        className="mx-auto w-16 h-16 rounded-full border-2 border-white/20 border-t-[var(--terra)]"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 0.9, ease: "linear" }}
                      />
                      <h3 className="font-display text-4xl sm:text-5xl mt-10 tracking-tight">Analyzing your profile…</h3>
                      <div className="mt-8 max-w-md mx-auto space-y-3 text-left">
                        {["Reviewing skin goals", "Matching active strengths", "Screening for safety", "Confirming eligibility"].map((t, i) => (
                          <motion.div
                            key={t}
                            initial={{ opacity: 0.2 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 + i * 0.5 }}
                            className="flex items-center gap-3 font-mono text-sm text-white/70"
                          >
                            <span className="text-[var(--terra)]">✓</span> {t}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {phase === "done" && (
                    <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }} className="text-center" data-testid="intake-result">
                      <Sparkles className="mx-auto text-[var(--terra)]" size={34} />
                      <h3 className="font-display text-5xl sm:text-7xl leading-[0.9] tracking-tighter mt-6">
                        Congratulations —<br />you're <span className="italic text-[var(--terra)]">eligible</span>.
                      </h3>
                      <p className="font-body text-white/60 mt-6 max-w-lg mx-auto leading-relaxed">
                        Based on your answers, our formulary matches your skin. Your
                        personalized pricing is unlocked and reserved.
                      </p>
                      <div className="mt-8 inline-flex items-center gap-3 glass-dark px-6 py-4 rounded-full">
                        <span className="overline text-white/60">Price locked for</span>
                        <Countdown />
                      </div>
                      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={() => { onClose(); setTimeout(() => scrollToId("skin"), 300); }}
                          className="btn-ink !bg-[var(--bone)] !text-[var(--ink)] px-8 py-4 rounded-full"
                          data-testid="intake-shop-btn"
                        >
                          Shop my matches
                        </button>
                        <button onClick={onClose} className="btn-outline !border-white/40 !text-[var(--bone)] px-8 py-4 rounded-full" data-testid="intake-done-close">
                          Maybe later
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
